// "Tamanho das caixas" (Ajustes > Aparência, texto 07 B3): Pequeno / Médio (padrão) / Grande.
// Vale para os cartões e linhas do app inteiro. Só mexe em espaçamentos, alturas mínimas, cantos e
// ícones/emojis grandes; o tamanho das letras continua com o "Tamanho do texto".
// Os números recebidos pelas funções são as medidas do visual de antes da 0.65 (o "Grande" antigo):
//   Pequeno = como estava (só o título, cabe mais na tela);
//   Médio   = entre o Médio antigo e o Grande novo, com título e descrição. É o padrão de quem nunca escolheu;
//   Grande  = um pouco maior do que o Grande antigo.
// 0.72 (texto 10 C4): o Médio ficou levemente maior (≈ +7%); Pequeno e Grande não mudaram.
import { useDoc } from '../store/store';

export type TamCaixas = 'g' | 'm' | 'p';
/** Sem valor guardado = a pessoa nunca escolheu = Médio. Quem escolheu mantém a escolha. */
export const TAM_CAIXAS_PADRAO: TamCaixas = 'm';

/** Medidas fixas usadas pelas peças de Ajustes (Linhas.tsx / comum.tsx). */
export function medidasCaixas(tam: TamCaixas | undefined) {
  switch (tam ?? TAM_CAIXAS_PADRAO) {
    case 'p': return { tam: 'p' as const, pad: 10, padH: 12, gap: 10, icone: 18, titulo: 14, resumo: false, linhaV: 8, raio: 12, card: 10, cardGap: 8 };
    case 'g': return { tam: 'g' as const, pad: 20, padH: 18, gap: 16, icone: 28, titulo: 17, resumo: true, linhaV: 14, raio: 18, card: 18, cardGap: 14 };
    default: return { tam: 'm' as const, pad: 16, padH: 16, gap: 14, icone: 24, titulo: 15, resumo: true, linhaV: 12, raio: 16, card: 15, cardGap: 12 };
  }
}

const esc = (n: number, f: number, min = 0) => Math.max(min, Math.round(n * f));
// fatores sobre a medida de antes: [Grande, Médio, Pequeno]
const F = {
  pad: [1.25, 1.02, 0.5], padH: [1.15, 1.0, 0.75], gap: [1.2, 1.02, 0.6], raio: [1.12, 1.0, 0.75], icone: [1.15, 1.0, 0.7], alt: [1.15, 1.02, 0.67],
} as const;

/** Ajudantes de medida conforme o tamanho escolhido. */
export function caixas(tam: TamCaixas | undefined) {
  const t: TamCaixas = tam ?? TAM_CAIXAS_PADRAO;
  const g = t === 'g', md = t === 'm';
  const i = g ? 0 : md ? 1 : 2;
  const f = (k: keyof typeof F, n: number, min: number) => (n === 0 ? 0 : esc(n, F[k][i], min));
  const vert = (n: number) => f('pad', n, n > 0 ? 2 : 0);
  const lat = (n: number) => f('padH', n, n > 0 ? 4 : 0);
  return {
    tam: t,
    grande: g, medio: md, pequeno: t === 'p',
    /** Preenchimento interno (vertical/geral): 16 → 20 / 16 / 8. */
    pad: vert,
    /** Preenchimento lateral (encolhe menos, para não colar o texto na borda): 16 → 18 / 16 / 12. */
    padH: lat,
    /** Preenchimento do cartão inteiro, para espalhar no estilo (vertical muda mais que o lateral). */
    pads: (n: number): { padding: number } | { paddingVertical: number; paddingHorizontal: number } =>
      ({ paddingVertical: vert(n), paddingHorizontal: lat(n) }),
    /** Como pads, mas só o vertical muda (linhas com texto/caixa de digitar). */
    padV: (n: number): { padding: number } | { paddingVertical: number; paddingHorizontal: number } =>
      ({ paddingVertical: vert(n), paddingHorizontal: n }),
    /** Espaço entre itens de uma lista / dentro do cartão: 10 → 12 / 10 / 6. */
    gap: (n: number) => f('gap', n, n > 0 ? 2 : 0),
    /** Cantos arredondados: 16 → 18 / 16 / 12. */
    raio: (n: number) => f('raio', n, n > 0 ? 4 : 0),
    /** Emojis/ícones grandes: 26 → 30 / 26 / 18. */
    icone: (n: number) => f('icone', n, 12),
    /** Altura mínima / tamanho de miniatura: 60 → 69 / 61 / 40. */
    alt: (n: number) => f('alt', n, 0),
    /** Mostra linhas de descrição decorativas (escondidas só no Pequeno). */
    resumo: t !== 'p',
    /** Escolhe um valor por tamanho. Os valores são os do visual de antes (Grande antigo, Médio antigo, Pequeno);
     *  números: Grande fica ~10% maior e Médio fica entre o Médio antigo e o Grande novo. */
    v: <T,>(gv: T, mv: T, pv: T): T => {
      if (t === 'p') return pv;
      if (typeof gv === 'number' && typeof mv === 'number') return (g ? Math.round(gv * 1.1) : Math.round(mv * 0.4 + gv * 1.1 * 0.6)) as unknown as T;   // Médio: um pouco mais perto do Grande (0.72)
      return g ? gv : mv;
    },
  };
}
export type Caixas = ReturnType<typeof caixas>;

/** Hook: ajudantes de medida do "Tamanho das caixas" atual. Chame no topo do componente. */
export const useCaixas = () => caixas(useDoc('ajustes').tamCaixas);
/** Hook: medidas fixas (formato antigo usado em Ajustes). */
export const useTamCaixas = () => medidasCaixas(useDoc('ajustes').tamCaixas);
